import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LifeBuoy,
  Plus,
  MessageSquare,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { supportTicketsApi, SupportTicketItem } from '@/api/http-client';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog';

export function PatientSupportTicketsPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketItem | null>(null);

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('GENERAL');
  const [description, setDescription] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: response, isLoading } = useQuery({
    queryKey: ['support', 'my-tickets'],
    queryFn: () => supportTicketsApi.getMyTickets(),
  });

  const tickets = response?.data || [];

  const createMutation = useMutation({
    mutationFn: supportTicketsApi.createTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support', 'my-tickets'] });
      setIsModalOpen(false);
      setSubject('');
      setDescription('');
      setCategory('GENERAL');
    },
    onError: (err: any) => {
      setErrorMsg(err?.message || 'Erro ao criar chamado. Tente novamente.');
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setErrorMsg('Preencha o assunto e os detalhes do chamado.');
      return;
    }
    setErrorMsg(null);
    createMutation.mutate({
      subject: subject.trim(),
      description: description.trim(),
      category,
      priority: category === 'CANCELLATION' ? 'HIGH' : 'MEDIUM',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'OPEN':
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">
            Aberto
          </span>
        );
      case 'IN_PROGRESS':
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20">
            Em Análise
          </span>
        );
      case 'RESOLVED':
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
            Resolvido
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground border border-border">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20 shadow-xs">
            <LifeBuoy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-foreground tracking-tight">Meus Chamados &amp; Suporte</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Acompanhe suas solicitações de cancelamento, dúvidas financeiras e atendimentos registrados pela Dra. Medi.
            </p>
          </div>
        </div>

        <Button
          onClick={() => {
            setIsModalOpen(true);
            setErrorMsg(null);
          }}
          className="rounded-xl font-bold text-xs h-10 px-5 gap-1.5 shadow-sm bg-primary text-white"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Chamado</span>
        </Button>
      </div>

      {/* Lista de Chamados */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span>Carregando seus chamados...</span>
        </div>
      ) : tickets.length === 0 ? (
        <div className="p-12 text-center rounded-3xl border border-dashed border-border bg-card/50 space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto">
            <MessageSquare className="w-6 h-6" />
          </div>
          <h3 className="font-bold text-foreground text-base">Você não possui chamados abertos</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            Precisa de ajuda com alguma consulta, cancelamento ou receita? Abra um chamado ou fale diretamente com a Dra. Medi no chat flutuante.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div
              key={t.id}
              onClick={() => setSelectedTicket(t)}
              className="p-5 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all cursor-pointer flex items-center justify-between gap-4"
            >
              <div className="space-y-1.5 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                    #{t.id.slice(0, 8)}
                  </span>
                  {getStatusBadge(t.status)}
                  {t.category && (
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase">
                      {t.category === 'CANCELLATION' ? 'Cancelamento/Estorno' : t.category}
                    </span>
                  )}
                </div>
                <h4 className="font-bold text-foreground text-sm truncate">{t.subject}</h4>
                <p className="text-xs text-muted-foreground line-clamp-1">{t.description}</p>
              </div>

              <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                <span className="hidden sm:inline">
                  {new Date(t.createdAt).toLocaleDateString('pt-BR')} às{' '}
                  {new Date(t.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Criação de Chamado */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg w-full rounded-3xl p-6 bg-card border border-border">
          <DialogHeader className="pb-3 border-b border-border">
            <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              <span>Abrir Novo Chamado de Suporte</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Nossa equipe humana responderá sua solicitação em horário comercial (8h às 18h).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-4 pt-3 text-xs">
            <div className="space-y-1">
              <label className="font-bold text-foreground">Categoria do Chamado *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
              >
                <option value="GENERAL">Dúvida Geral / Atendimento</option>
                <option value="CANCELLATION">Solicitação de Cancelamento / Reembolso</option>
                <option value="PAYMENT">Pagamento / PIX / Cartão</option>
                <option value="PRESCRIPTION">Receita Médica / Atestado</option>
                <option value="TECHNICAL">Problema Técnico na Sala de Vídeo</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-foreground">Assunto *</label>
              <input
                type="text"
                placeholder="Ex: Cancelamento de consulta de amanhã"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full h-10 px-3.5 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-foreground">Descrição Detalhada *</label>
              <textarea
                rows={4}
                placeholder="Descreva seu caso com detalhes para agilizar o atendimento..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-3 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              />
            </div>

            {errorMsg && <p className="text-xs text-rose-500 font-semibold">{errorMsg}</p>}

            <div className="pt-2 flex justify-end gap-2 border-t border-border">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl font-semibold">
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending} className="rounded-xl font-bold bg-primary text-white">
                {createMutation.isPending ? 'Enviando...' : 'Registrar Chamado'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes do Chamado */}
      {selectedTicket && (
        <Dialog open={Boolean(selectedTicket)} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-lg w-full rounded-3xl p-6 bg-card border border-border space-y-4">
            <DialogHeader className="pb-3 border-b border-border">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                  Protocolo #{selectedTicket.id.slice(0, 8)}
                </span>
                {getStatusBadge(selectedTicket.status)}
              </div>
              <DialogTitle className="text-base font-bold text-foreground pt-1">{selectedTicket.subject}</DialogTitle>
            </DialogHeader>

            <div className="space-y-3 text-xs leading-relaxed">
              <div className="p-3.5 rounded-xl bg-muted/30 border border-border text-foreground">
                <span className="text-[10px] font-bold uppercase text-muted-foreground block mb-1">Descrição:</span>
                <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-muted-foreground">
                <div>
                  <span className="block text-[10px] font-bold uppercase">Aberto em:</span>
                  <span className="font-semibold text-foreground">
                    {new Date(selectedTicket.createdAt).toLocaleString('pt-BR')}
                  </span>
                </div>
                {selectedTicket.resolvedAt && (
                  <div>
                    <span className="block text-[10px] font-bold uppercase">Resolvido em:</span>
                    <span className="font-semibold text-emerald-600">
                      {new Date(selectedTicket.resolvedAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
